<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  iAlertMessage,
  iAlertMessageType,
  iAlertMessageSize,
} from '@inscribe/signal';
import {
  InsightConditionType,
  InsightCondition,
  ConfigurableInsightLayoutType,
  OperatorValueType,
  DailyBalanceConditionOperator,
  TransactionConditionOperator,
  type ConfigurableInsight,
} from '@/types/decision-engine/configurable-insights.types';
import TransactionConditionEditor from '@/V2/decision-engine/configurable-insights/components/TransactionConditionEditor.vue';
import DailyBalanceConditionEditor from '@/V2/decision-engine/configurable-insights/components/DailyBalanceConditionEditor.vue';
import ConditionTypeSelector from '@/V2/decision-engine/configurable-insights/components/ConditionTypeSelector.vue';

interface Props {
  insightType: ConfigurableInsightLayoutType;
  insight: ConfigurableInsight;
  isEditMode?: boolean;
}

const props = defineProps<Props>();

// Helpers

const createDefaultCondition = (
  type: InsightConditionType,
): InsightCondition => {
  if (type === InsightConditionType.DAILY_BALANCE) {
    return {
      condition_type: type,
      operator: DailyBalanceConditionOperator.MINIMUM,
      operator_value_type: OperatorValueType.ABSOLUTE,
      operator_value: null,
      filters: {},
    } as InsightCondition;
  }

  // Transaction condition with ANY operator
  return {
    condition_type: type,
    operator: TransactionConditionOperator.ANY,
    operator_value_type: null,
    operator_value: null,
    filters: {},
  } as InsightCondition;
};

const initializeConditions = () => {
  // Only initialize if creating a new insight (not in edit mode)
  if (!props.isEditMode) {
    props.insight.configuration.conditions = [];
    let insightType = InsightConditionType.TRANSACTIONS;
    if (props.insightType === ConfigurableInsightLayoutType.FLAG_BALANCE_ONLY) {
      insightType = InsightConditionType.DAILY_BALANCE;
    }
    props.insight.configuration.conditions.push(
      createDefaultCondition(insightType),
    );
  }
};

// Watchers
watch(
  () => props.insightType,
  () => {
    initializeConditions();
  },
  { immediate: true },
);

// Computed
const isCombinedMode = computed(
  () => props.insightType === ConfigurableInsightLayoutType.FLAG_COMBINED,
);
const canRemoveCondition = computed(
  () =>
    isCombinedMode.value && props.insight.configuration.conditions.length > 1,
);

// Handlers
const onAddCondition = () => {
  // Default to TRANSACTIONS type when adding a new condition in COMBINED mode
  const newCondition = createDefaultCondition(
    InsightConditionType.TRANSACTIONS,
  );
  props.insight.configuration.conditions.push(newCondition);
};

const onConditionUpdate = (index: number, updated: InsightCondition) => {
  props.insight.configuration.conditions.splice(index, 1, updated);
};

const onRemoveCondition = (index: number) => {
  props.insight.configuration.conditions.splice(index, 1);
};

const onConditionTypeChange = (index: number, type: InsightConditionType) => {
  const newCondition = createDefaultCondition(type);
  props.insight.configuration.conditions.splice(index, 1, newCondition);
};

// Lifecycle hooks
onMounted(() => {
  initializeConditions();
});
</script>

<template>
  <div class="insight-logic-section d-flex flex-column i-gap-8">
    <h3 class="i-text-heading-6 title">Logic</h3>
    <div
      class="logic-container d-flex flex-column i-gap-10 i-p-11 i-radius-5xl"
    >
      <!-- limitations alert -->
      <i-alert-message
        v-if="insight.limitations"
        data-testid="limitations-alert"
        :type="iAlertMessageType.NEUTRAL"
        :size="iAlertMessageSize.MEDIUM"
        heading="Logic Limitation"
        :message="insight.limitations"
      />

      <!-- conditions list -->
      <div class="conditions-list d-flex flex-column i-gap-9">
        <template
          v-for="(condition, index) in insight.configuration.conditions"
          :key="index"
        >
          <div
            class="condition-content d-flex flex-column i-gap-8"
            :class="{
              'with-wrapper': isCombinedMode,
              'i-p-11': isCombinedMode,
              'i-radius-3xl': isCombinedMode,
            }"
          >
            <!-- Condition type selector and remove button for COMBINED mode -->
            <div
              v-if="isCombinedMode"
              class="condition-controls d-flex align-items-center"
            >
              <div class="condition-type-wrapper">
                <ConditionTypeSelector
                  :model-value="condition.condition_type"
                  @update:model-value="
                    (type) => onConditionTypeChange(index, type)
                  "
                />
              </div>
              <i-button
                v-if="canRemoveCondition"
                class="flex-shrink-0"
                :button-style="iButtonStyles.GHOST"
                :type="iButtonTypes.NEUTRAL"
                :size="iButtonSizes.LARGE"
                left-icon="medium-delete-bin-1"
                data-testid="remove-condition-button"
                data-dd-action-name="remove-condition-button"
                @button-click="onRemoveCondition(index)"
              />
            </div>

            <TransactionConditionEditor
              v-if="
                condition.condition_type === InsightConditionType.TRANSACTIONS
              "
              :condition="condition"
              @update:condition="(updated) => onConditionUpdate(index, updated)"
            />
            <DailyBalanceConditionEditor
              v-else-if="
                condition.condition_type === InsightConditionType.DAILY_BALANCE
              "
              :condition="condition"
              @update:condition="(updated) => onConditionUpdate(index, updated)"
            />
          </div>

          <!-- AND separator between conditions -->
          <div
            v-if="
              isCombinedMode &&
              index < insight.configuration.conditions.length - 1
            "
            class="condition-separator i-text-button-2"
          >
            And
          </div>
        </template>
      </div>

      <!-- add condition button -->
      <i-button
        v-if="isCombinedMode"
        :button-style="iButtonStyles.OUTLINE"
        :type="iButtonTypes.NEUTRAL"
        :size="iButtonSizes.MEDIUM"
        text="Add Condition"
        class="add-condition-button"
        data-testid="add-condition-button"
        data-dd-action-name="add-condition-button"
        @click="onAddCondition"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.insight-logic-section {
  .title {
    color: var(--text-neutral-bold);
  }

  .logic-container {
    background: var(--base);
    border: 1px solid var(--border-neutral-subtle);
    overflow: hidden;

    :deep(.header) {
      .i-select:not(.custom-width) {
        width: 160px;
      }
    }

    .condition-controls {
      justify-content: space-between;

      .condition-type-wrapper {
        width: 200px;
        flex-shrink: 0;
      }
    }

    .condition-content {
      &.with-wrapper {
        border: 1px solid var(--border-neutral-subtle);
      }

      :deep(.active-filters) {
        background: var(--surface-neutral-subtle);

        .filter-section {
          flex: 1;
          min-width: 0;
        }
      }
    }

    .add-condition-button {
      width: fit-content;
    }

    .condition-separator {
      color: var(--text-neutral-bold);
      text-align: left;
    }
  }
}
</style>
